import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, TrendingUp, CreditCard, Handshake, LogOut, ChevronDown, Wallet, Bot, Target, Flag, Plane, Sparkles, Bell, BellOff, Fingerprint, Lock, Settings, LineChart, Globe, Scale, BookOpen } from "lucide-react";
import { logger } from "@/lib/logger";
import { CURRENCIES } from "@/lib/api";
import { api } from "@/lib/api";
import AssistantWidget from "@/components/AssistantWidget";
import BiometricLock from "@/components/BiometricLock";
import NotificationsDialog from "@/components/NotificationsDialog";
import NotificationHistoryDialog from "@/components/NotificationHistoryDialog";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  isPushSupported,
  pushPermission,
  getCurrentSubscription,
  enablePush,
  disablePush,
  sendTestPush,
} from "@/lib/push";
import {
  isNativeApp,
  biometricSupported,
  biometricPreference,
  setBiometricPreference,
  biometricAuthenticate,
} from "@/lib/biometric";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

const NavItem = ({ to, icon: Icon, label, testid }) => (
  <NavLink
    to={to}
    data-testid={testid}
    title={label}
    className={({ isActive }) =>
      `flex items-center gap-2 px-2.5 py-2 rounded-xl text-sm font-medium transition-colors duration-200 shrink-0 ${
        isActive
          ? "bg-[#0F291E] text-white"
          : "text-[#5F7368] hover:text-[#0A110E] hover:bg-[#EBEBEA]/60"
      }`
    }
  >
    {({ isActive }) => (
      <>
        <Icon className="w-4 h-4 shrink-0" />
        {isActive && <span>{label}</span>}
      </>
    )}
  </NavLink>
);

export default function AppShell({ children }) {
  const { user, setUser, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [pushEnabled, setPushEnabled] = React.useState(false);
  const [pushSupported, setPushSupported] = React.useState(false);
  const [bioAvailable, setBioAvailable] = React.useState(false);
  const [bioEnabled, setBioEnabled] = React.useState(false);
  const [prefsOpen, setPrefsOpen] = React.useState(false);
  const [historyOpen, setHistoryOpen] = React.useState(false);
  const nativeApp = isNativeApp();

  const currentLang = (i18n.resolvedLanguage || i18n.language || "en").split("-")[0];
  const changeLang = async (lng) => {
    if (lng === currentLang) return;
    try { await i18n.changeLanguage(lng); } catch (e) { logger.warn("i18n change", e); }
    try {
      const r = await api.patch("/auth/me/language", { language: lng });
      if (r.data?.language) setUser({ ...user, language: r.data.language });
    } catch (e) {
      // Non-fatal: language still applied locally via localStorage.
      logger.warn("persist language", e);
    }
  };

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const supported = isPushSupported();
      if (cancelled) return;
      setPushSupported(supported);
      if (supported) {
        const sub = await getCurrentSubscription();
        if (!cancelled) setPushEnabled(!!sub && pushPermission() === "granted");
      }
      const bio = await biometricSupported();
      if (!cancelled) {
        setBioAvailable(bio);
        setBioEnabled(biometricPreference());
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Background refresh-all every 6h while the app is open. Silent; only logs
  // errors. Also refreshes on tab visibility change if we suspect a long idle.
  React.useEffect(() => {
    const SIX_HOURS = 6 * 60 * 60 * 1000;
    let stopped = false;

    const tick = async () => {
      if (stopped) return;
      try {
        const status = await api.get("/investments/refresh-status");
        if (status.data?.stale) {
          await api.post("/investments/refresh-all");
        }
      } catch (e) {
        logger.warn("scheduled refresh-all", e);
      }
    };

    const intervalId = setInterval(tick, SIX_HOURS);
    const onVisible = () => { if (document.visibilityState === "visible") tick(); };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      stopped = true;
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  const toggleBiometric = async () => {
    try {
      if (!nativeApp) {
        toast.info(t("shell.biometricNotInApp"));
        return;
      }
      if (bioEnabled) {
        // Require a fresh auth before disabling to avoid casual bypass
        await biometricAuthenticate({ reason: t("shell.biometricPromptDisable") });
        setBiometricPreference(false);
        setBioEnabled(false);
        toast.success(t("shell.biometricDisabled"));
      } else {
        await biometricAuthenticate({ reason: t("shell.biometricPromptEnable") });
        setBiometricPreference(true);
        setBioEnabled(true);
        toast.success(t("shell.biometricEnabled"));
      }
    } catch (e) {
      toast.error(t("shell.authCancelled"));
    }
  };

  const togglePush = async () => {
    try {
      if (pushEnabled) {
        await disablePush();
        setPushEnabled(false);
        toast.success(t("shell.notifOff"));
      } else {
        await enablePush();
        setPushEnabled(true);
        toast.success(t("shell.notifOn"));
        try { await sendTestPush(); } catch (e) { logger.warn("test push", e); }
      }
    } catch (e) {
      if (e.message === "PermissionDenied") {
        toast.error(t("shell.notifPermDenied"));
      } else if (e.message === "PushNotSupported") {
        toast.error(t("shell.notifUnsupported"));
      } else {
        toast.error(t("shell.notifSetFailed"));
      }
    }
  };

  const lockNow = () => {
    try {
      // Force the BiometricLock overlay by flipping a session flag it listens to
      sessionStorage.setItem("force_lock", "1");
      window.dispatchEvent(new Event("bio-lock"));
    } catch (e) {
      // sessionStorage may be unavailable (private mode / SSR). Non-fatal.
      logger.warn("lock now failed", e);
    }
  };

  const setCurrency = async (code) => {
    try {
      await api.patch("/auth/me/currency", { base_currency: code });
      setUser({ ...user, base_currency: code });
    } catch (e) {
      logger.error("set currency", e);
    }
  };

  return (
    <div className="min-h-screen w-full bg-[#FAFAF9]">
      {/* Top nav */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#FAFAF9]/85 border-b border-[#EBEBEA]">
        <div className="mx-auto px-4 lg:px-8 py-4 flex items-center gap-4 max-w-[1600px]">
          <div
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 cursor-pointer"
            data-testid="brand-logo"
          >
            <div className="w-8 h-8 rounded-lg bg-[#0F291E] flex items-center justify-center">
              <TrendingUp className="w-4 h-4 text-white" />
            </div>
            <span className="font-display text-lg font-semibold text-[#0A110E] tracking-tight">{t("shell.brand")}</span>
          </div>

          <nav className="hidden md:flex items-center gap-0.5 ml-2 flex-1 min-w-0 overflow-x-auto scrollbar-thin">
            <NavItem to="/dashboard" icon={LayoutDashboard} label={t("nav.dashboard")} testid="nav-dashboard" />
            <NavItem to="/accounts" icon={Wallet} label={t("nav.accounts")} testid="nav-accounts" />
            <NavItem to="/investments" icon={TrendingUp} label={t("nav.investments")} testid="nav-investments" />
            <NavItem to="/rebalance" icon={Scale} label={t("nav.rebalance")} testid="nav-rebalance" />
            <NavItem to="/watchlist" icon={LineChart} label={t("nav.watchlist")} testid="nav-watchlist" />
            <NavItem to="/debts" icon={CreditCard} label={t("nav.debts")} testid="nav-debts" />
            <NavItem to="/loans" icon={Handshake} label={t("nav.loans")} testid="nav-loans" />
            <NavItem to="/budgets" icon={Target} label={t("nav.budgets")} testid="nav-budgets" />
            <NavItem to="/goals" icon={Flag} label={t("nav.goals")} testid="nav-goals" />
            <NavItem to="/trips" icon={Plane} label={t("nav.trips")} testid="nav-trips" />
            <NavItem to="/rules" icon={Bot} label={t("nav.rules")} testid="nav-rules" />
            <NavItem to="/assistant" icon={Sparkles} label={t("nav.assistant")} testid="nav-assistant" />
          </nav>

          <div className="flex items-center gap-3 shrink-0">
            {/* Language selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="lang-selector"
                  title={t("lang.title")}
                  className="hidden sm:flex items-center gap-1 px-3 py-2 rounded-xl border border-[#EBEBEA] bg-white text-sm text-[#0A110E] hover:border-[#1C7C54]/40 transition-colors"
                >
                  <Globe className="w-3.5 h-3.5 text-[#5F7368]" />
                  <span className="font-medium uppercase">{currentLang}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-[#8A9992]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{t("lang.title")}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => changeLang("en")}
                  data-testid="lang-option-en"
                >
                  <span className="w-8 tabular-nums uppercase text-[#5F7368]">EN</span>
                  <span className="text-sm">{t("lang.en")}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => changeLang("es")}
                  data-testid="lang-option-es"
                >
                  <span className="w-8 tabular-nums uppercase text-[#5F7368]">ES</span>
                  <span className="text-sm">{t("lang.es")}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Currency selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="currency-selector"
                  className="flex items-center gap-1 px-3 py-2 rounded-xl border border-[#EBEBEA] bg-white text-sm text-[#0A110E] hover:border-[#1C7C54]/40 transition-colors"
                >
                  <span className="tabular-nums font-medium">{user?.base_currency || "USD"}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-[#8A9992]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{t("shell.baseCurrency")}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {CURRENCIES.map((c) => (
                  <DropdownMenuItem
                    key={c.code}
                    onClick={() => setCurrency(c.code)}
                    data-testid={`currency-option-${c.code}`}
                  >
                    <span className="w-8 tabular-nums text-[#5F7368]">{c.code}</span>
                    <span className="text-sm">{c.label}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Direct logout button — always visible so users never have to
                hunt inside the avatar dropdown. Preserves the dropdown menu
                below for preferences/notifications. */}
            <button
              type="button"
              data-testid="logout-quick-btn"
              onClick={logout}
              title={t("nav.logout")}
              className="hidden sm:inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-[#EBEBEA] bg-white text-sm font-medium text-[#D95D39] hover:border-[#D95D39]/40 hover:bg-[#D95D39]/5 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden md:inline">{t("nav.logoutShort")}</span>
            </button>

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu"
                  title={user?.name || user?.email || t("shell.userAccountFallback")}
                  className="w-9 h-9 rounded-full border border-[#EBEBEA] bg-white hover:border-[#1C7C54]/40 transition-colors overflow-hidden"
                >
                  {user?.picture ? (
                    <img src={user.picture} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-[#0F291E] text-white flex items-center justify-center text-sm font-semibold">
                      {user?.name?.[0]?.toUpperCase() || "U"}
                    </div>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold text-[#0A110E]">{user?.name}</div>
                  <div className="text-xs text-[#8A9992]">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {pushSupported && (
                  <DropdownMenuItem
                    onClick={togglePush}
                    data-testid="push-toggle-btn"
                    onSelect={(e) => e.preventDefault()}
                  >
                    {pushEnabled ? (
                      <><BellOff className="w-4 h-4 mr-2" /> {t("shell.disableNotifications")}</>
                    ) : (
                      <><Bell className="w-4 h-4 mr-2" /> {t("shell.enableNotifications")}</>
                    )}
                  </DropdownMenuItem>
                )}
                {/* Language quick switch inside user menu (visible on mobile too) */}
                <DropdownMenuItem
                  onClick={() => changeLang(currentLang === "en" ? "es" : "en")}
                  data-testid="lang-quick-toggle"
                  onSelect={(e) => e.preventDefault()}
                >
                  <Globe className="w-4 h-4 mr-2" />
                  {t("lang.title")}: {currentLang === "en" ? t("lang.en") : t("lang.es")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setPrefsOpen(true)}
                  data-testid="open-prefs-btn"
                >
                  <Settings className="w-4 h-4 mr-2" /> {t("shell.preferences")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setHistoryOpen(true)}
                  data-testid="open-notif-history-btn"
                >
                  <Bell className="w-4 h-4 mr-2" /> {t("shell.notifHistory")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  asChild
                  data-testid="open-manual-btn"
                >
                  <a
                    href={currentLang === "es" ? "/manual.pdf" : "/manual-en.pdf"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center w-full"
                  >
                    <BookOpen className="w-4 h-4 mr-2" /> {t("shell.userManual")}
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={toggleBiometric}
                  data-testid="biometric-toggle-btn"
                  onSelect={(e) => e.preventDefault()}
                  disabled={!nativeApp || !bioAvailable}
                  title={!nativeApp ? t("shell.biometricNativeOnly") : ""}
                >
                  <Fingerprint className="w-4 h-4 mr-2" />
                  {bioEnabled ? t("shell.disableBiometric") : t("shell.enableBiometric")}
                </DropdownMenuItem>
                {bioEnabled && nativeApp && (
                  <DropdownMenuItem onClick={lockNow} data-testid="lock-now-btn">
                    <Lock className="w-4 h-4 mr-2" /> {t("shell.lockNow")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={logout}
                  data-testid="logout-btn"
                  className="text-[#D95D39] focus:text-[#D95D39] focus:bg-[#D95D39]/10"
                >
                  <LogOut className="w-4 h-4 mr-2" /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        {/* Mobile nav */}
        <div className="md:hidden border-t border-[#EBEBEA] px-4 py-2 flex gap-1 overflow-x-auto">
          <NavItem to="/dashboard" icon={LayoutDashboard} label={t("nav.dashboard")} testid="nav-dashboard-m" />
          <NavItem to="/accounts" icon={Wallet} label={t("nav.accounts")} testid="nav-accounts-m" />
          <NavItem to="/investments" icon={TrendingUp} label={t("nav.investments")} testid="nav-investments-m" />
          <NavItem to="/rebalance" icon={Scale} label={t("nav.rebalance")} testid="nav-rebalance-m" />
          <NavItem to="/debts" icon={CreditCard} label={t("nav.debts")} testid="nav-debts-m" />
          <NavItem to="/loans" icon={Handshake} label={t("nav.loans")} testid="nav-loans-m" />
          <NavItem to="/budgets" icon={Target} label={t("nav.budgets")} testid="nav-budgets-m" />
          <NavItem to="/goals" icon={Flag} label={t("nav.goals")} testid="nav-goals-m" />
          <NavItem to="/trips" icon={Plane} label={t("nav.trips")} testid="nav-trips-m" />
          <NavItem to="/rules" icon={Bot} label={t("nav.rules")} testid="nav-rules-m" />
          <NavItem to="/assistant" icon={Sparkles} label={t("nav.assistant")} testid="nav-assistant-m" />
          <button
            type="button"
            data-testid="logout-quick-mobile-btn"
            onClick={logout}
            className="ml-auto shrink-0 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-sm font-medium text-[#D95D39] hover:bg-[#D95D39]/10"
          >
            <LogOut className="w-4 h-4" /> {t("nav.logoutShort")}
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <BiometricLock>{children}</BiometricLock>
      </main>
      <AssistantWidget />
      <NotificationsDialog open={prefsOpen} onOpenChange={setPrefsOpen} />
      <NotificationHistoryDialog open={historyOpen} onOpenChange={setHistoryOpen} />
    </div>
  );
}
